import React, { useMemo, useState } from 'react';
import { 
  Card, 
  InputNumber, 
  Button, 
  Row, 
  Col, 
  Alert, 
  Space, 
  Statistic, 
  Divider, 
  Spin, 
  message,
  Table,
} from 'antd';
import { Database, AlertTriangle, Settings, Edit2, Save, X } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import { useUpdateStorageSettings } from '~/services/location/location.mutations';
import { useGetStorageSettings } from '~/services/location/location.queries';
import { useGetInventoryMetrics } from '~/services/inventory/inventory.queries';
import { useGetBusinessAddresses } from '~/services/business-addresses/business-addresses.queries';
import { useUpdateBusinessAddress } from '~/services/business-addresses/business-addresses.queries';
import { TBusinessAddress, AddressType } from '~/types/business-address';

export const StorageBufferTab: React.FC = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingCapacity, setEditingCapacity] = useState<number | null>(null);
  
  const { data: storageSettings, isLoading: isLoadingSettings } = useGetStorageSettings();
  const { data: inventoryMetrics, isLoading: isLoadingMetrics } = useGetInventoryMetrics();
  const { data: businessAddresses, isLoading: isLoadingAddresses, refetch: refetchAddresses } = useGetBusinessAddresses(false);
  const updateStorageSettings = useUpdateStorageSettings();
  const updateBusinessAddress = useUpdateBusinessAddress();

  // Filter to only storage-type addresses
  const storageAddresses = useMemo(() => {
    return (businessAddresses || []).filter(addr => addr.addressType === AddressType.STORAGE);
  }, [businessAddresses]);

  // Calculate total capacity from all storage addresses
  const totalCapacity = useMemo(() => {
    const total = storageAddresses.reduce((sum, addr) => {
      // Handle both number and string (DECIMAL from DB might be string)
      const capacity = typeof addr.storageCapacity === 'string' 
        ? parseFloat(addr.storageCapacity) 
        : (addr.storageCapacity || 0);
      const numericCapacity = isNaN(capacity) ? 0 : capacity;
      return sum + numericCapacity;
    }, 0);
    return total;
  }, [storageAddresses]);

  // Calculate real storage data
  const usedSpace = useMemo(() => {
    return inventoryMetrics?.totalStorageSpace || 0;
  }, [inventoryMetrics]);

  const bufferPercentage = storageSettings?.storageBufferPercentage || 15;
  
  // Storage buffer is applied as: inventory_cubic_ft * (1 + buffer_percentage)
  const usedSpaceWithBuffer = usedSpace * (1 + bufferPercentage / 100);
  const availableSpace = totalCapacity > 0 ? totalCapacity - usedSpaceWithBuffer : null;
  const currentUtilization = totalCapacity > 0 
    ? (usedSpaceWithBuffer / totalCapacity) * 100 
    : null;

  const getUtilizationColor = (utilization: number) => {
    if (utilization > 90) return 'text-red-600';
    if (utilization > 75) return 'text-orange-600';
    if (utilization > 55) return 'text-green-600';
    if (utilization > 35) return 'text-blue-600';
    if (utilization > 20) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getUtilizationStatus = (utilization: number) => {
    if (utilization > 90) return 'Approaching Capacity';
    if (utilization > 75) return 'Great';
    if (utilization > 55) return 'Good';
    if (utilization > 35) return 'OK';
    if (utilization > 20) return 'Bad';
    return 'Terrible';
  };

  const handleEditCapacity = (record: TBusinessAddress) => {
    setEditingId(record.id);
    // Handle both number and string (DECIMAL from DB might be string)
    const capacity = typeof record.storageCapacity === 'string' 
      ? parseFloat(record.storageCapacity) 
      : (record.storageCapacity || null);
    setEditingCapacity(isNaN(capacity) ? null : capacity);
  };

  const handleSaveCapacity = async (id: string) => {
    try {
      await updateBusinessAddress.mutateAsync({
        id,
        data: { storageCapacity: editingCapacity },
      });
      message.success('Storage capacity updated successfully');
      setEditingId(null);
      setEditingCapacity(null);
      // Manually refetch to ensure data is up to date
      await refetchAddresses();
    } catch (error) {
      message.error('Failed to update storage capacity');
      console.error('Error updating capacity:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingCapacity(null);
  };

  const handleSaveBufferSettings = async (bufferPercentage: number) => {
    try {
      await updateStorageSettings.mutateAsync({
        storageBufferPercentage: bufferPercentage,
        billingPeriodRounding: storageSettings?.billingPeriodRounding || 'ROUND_UP',
      });
      message.success('Buffer percentage saved successfully');
    } catch (error) {
      message.error('Failed to save buffer settings');
      console.error('Error saving settings:', error);
    }
  };

  const columns: ColumnsType<TBusinessAddress> = [
    {
      title: 'Storage Location',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-xs text-gray-500">
            {record.address}, {record.city}, {record.state}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <span className={isActive ? 'text-green-600' : 'text-gray-400'}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Storage Capacity (ft³)',
      key: 'storageCapacity',
      width: 200,
      render: (_, record) => {
        const isEditing = editingId === record.id;
        // Handle both number and string (DECIMAL from DB might be string)
        const capacity = typeof record.storageCapacity === 'string' 
          ? parseFloat(record.storageCapacity) 
          : (record.storageCapacity || null);
        
        if (isEditing) {
          return (
            <InputNumber
              value={editingCapacity}
              onChange={(value) => setEditingCapacity(value || null)}
              min={0}
              step={100}
              className="w-full"
              addonAfter="ft³"
              autoFocus
            />
          );
        }
        return (
          <div className="flex items-center justify-between">
            <span>{capacity !== null && !isNaN(capacity) ? capacity.toLocaleString() : 'Not set'}</span>
            <Button
              type="text"
              size="small"
              icon={<Edit2 size={14} />}
              onClick={() => handleEditCapacity(record)}
            />
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        const isEditing = editingId === record.id;
        if (isEditing) {
          return (
            <Space>
              <Button
                type="text"
                size="small"
                icon={<Save size={14} />}
                onClick={() => handleSaveCapacity(record.id)}
                loading={updateBusinessAddress.isPending}
              />
              <Button
                type="text"
                size="small"
                icon={<X size={14} />}
                onClick={handleCancelEdit}
              />
            </Space>
          );
        }
        return null;
      },
    },
  ];

  const isLoading = isLoadingSettings || isLoadingMetrics || isLoadingAddresses;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Storage KPI</h3>
        <p className="text-gray-500">Monitor storage capacity and utilization across all storage locations</p>
      </div>


      <Row gutter={24}>
        {/* Current Status */}
        <Col span={16}>
          <Card title="Storage Overview" className="mb-6">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Capacity"
                  value={totalCapacity > 0 ? totalCapacity.toLocaleString() : 'Not Set'}
                  suffix={totalCapacity > 0 ? "ft³" : ""}
                  prefix={<Database size={16} />}
                  valueStyle={{ color: totalCapacity > 0 ? undefined : '#999' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  {storageAddresses.length} location{storageAddresses.length !== 1 ? 's' : ''}
                </div>
              </Col>
              <Col span={6}>
                <Statistic
                  title="Used Space (with buffer)"
                  value={usedSpaceWithBuffer.toFixed(0)}
                  suffix="ft³"
                  valueStyle={{ color: '#1890ff' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Base: {usedSpace.toFixed(0)} ft³
                </div>
              </Col>
              <Col span={6}>
                <Statistic
                  title="Buffer Applied"
                  value={(usedSpaceWithBuffer - usedSpace).toFixed(0)}
                  suffix="ft³"
                  valueStyle={{ color: '#52c41a' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  {bufferPercentage}% of base
                </div>
              </Col>
              <Col span={6}>
                <Statistic
                  title="Available Space"
                  value={availableSpace !== null ? availableSpace.toFixed(0) : 'N/A'}
                  suffix={availableSpace !== null ? "ft³" : ""}
                  valueStyle={{ 
                    color: availableSpace !== null 
                      ? (availableSpace < 0 ? '#ff4d4f' : '#52c41a')
                      : '#999'
                  }}
                />
              </Col>
            </Row>

            <Divider />

            {totalCapacity > 0 && currentUtilization !== null ? (
              <>
            <div className="text-center">
              <div className={`text-4xl font-bold mb-2 ${getUtilizationColor(currentUtilization)}`}>
                    {currentUtilization.toFixed(1)}%
              </div>
              <div className="text-lg text-gray-600">Storage Utilization</div>
              <div className={`text-sm mt-1 ${getUtilizationColor(currentUtilization)}`}>
                Status: {getUtilizationStatus(currentUtilization)}
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="mt-6">
              <div className="bg-gray-200 rounded-full h-6 relative">
                <div
                  className="bg-blue-500 h-6 rounded-l-full transition-all duration-300"
                      style={{ width: `${Math.min(100, (usedSpaceWithBuffer / totalCapacity) * 100)}%` }}
                    />
                    {availableSpace !== null && availableSpace < 0 && (
                      <div className="absolute top-0 right-0 bg-red-500 h-6 rounded-r-full" 
                        style={{ width: `${Math.abs((availableSpace / totalCapacity) * 100)}%` }} 
                      />
                )}
              </div>
              <div className="flex justify-between mt-2 text-sm text-gray-600">
                    <span>Used (with buffer): {usedSpaceWithBuffer.toFixed(0)} ft³</span>
                    <span>Available: {availableSpace !== null ? availableSpace.toFixed(0) : 'N/A'} ft³</span>
                <span>Total: {totalCapacity.toLocaleString()} ft³</span>
              </div>
            </div>

                {availableSpace !== null && availableSpace < 0 && (
                  <Alert
                    message="Storage Over Capacity"
                    description={`Storage is over capacity by ${Math.abs(availableSpace).toFixed(0)} ft³. Consider increasing capacity or reducing inventory.`}
                    type="error"
                    showIcon
                    icon={<AlertTriangle size={16} />}
                    className="mt-4"
                  />
                )}
                
                <Divider />
                
                <div className="text-xs text-gray-500 mt-4">
                  Storage capacity is tracked per storage-type business address. The buffer percentage is a calculation multiplier applied to inventory cubic footage (e.g., 100 ft³ × 1.1 = 110 ft³) for storage calculations, not billing rates.
                </div>
              </>
            ) : (
              <>
                <Alert
                  message="No Storage Capacity Set"
                  description={
                    <div>
                      <p>Add storage capacity to your storage-type business addresses below to track utilization.</p>
                      {storageAddresses.length === 0 && (
                        <p className="mt-2 text-sm">No storage-type business addresses found. Create business addresses with type 'storage' first.</p>
                      )}
                      {storageAddresses.length > 0 && totalCapacity === 0 && (
                        <p className="mt-2 text-sm">
                          Found {storageAddresses.length} storage location(s), but no capacity values set. 
                          Set capacity values in the table below.
                        </p>
                      )}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="mt-2 text-xs font-mono bg-gray-100 p-2 rounded">
                          Debug: Total Capacity = {totalCapacity}, Storage Addresses = {storageAddresses.length}
                          <br />
                          Addresses: {JSON.stringify(storageAddresses.map(a => ({ name: a.name, capacity: a.storageCapacity })))}
                        </div>
                      )}
                    </div>
                  }
                  type="warning"
                  showIcon
                  className="mt-4"
                />
                
                <Divider />
                
                <div className="text-xs text-gray-500 mt-4">
                  Storage capacity is tracked per storage-type business address. The buffer percentage is a calculation multiplier applied to inventory cubic footage (e.g., 100 ft³ × 1.1 = 110 ft³) for storage calculations, not billing rates.
                </div>
              </>
            )}
          </Card>
        </Col>

        {/* Buffer Settings */}
        <Col span={8}>
          <Card title="Buffer Settings" extra={<Settings size={16} />}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Buffer Percentage
                </label>
                <InputNumber
                  value={bufferPercentage}
                  onChange={(value) => {
                    if (value !== null) {
                      handleSaveBufferSettings(value);
                    }
                  }}
                  min={1}
                  max={50}
                  step={1}
                  className="w-full"
                  addonAfter="%"
                  disabled={updateStorageSettings.isPending}
                />
                <div className="text-xs text-gray-500 mt-2">
                  Applied as: inventory_cubic_ft × (1 + {bufferPercentage/100}) = {(1 + bufferPercentage/100).toFixed(2)}×
                </div>
              </div>

              <Alert
                message="Buffer Calculation"
                description={`Buffer (${bufferPercentage}%) is a multiplier used for storage space calculations, not billing rates.`}
                type="info"
                showIcon
                size="small"
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Storage Locations Table */}
      <Card title="Storage Locations">
        {storageAddresses.length === 0 ? (
          <Alert
            message="No Storage Locations"
            description="Create business addresses with type 'storage' to track capacity per location."
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={storageAddresses}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}
      </Card>

    </div>
  );
}; 
